import { Inject } from '@odinjs/odin';
import { ButtonAPI, FieldListAPI, FormAPI, PanelAPI, TextAreaAPI } from '@philips-internal/oswald-ui-components';
import { ContextType, Value } from '@philips-internal/oswald-ui-core';
import Controller, { OnClick, OnValidate } from '@philips-internal/oswald-ui-core/lib/client';
import { SubmittableFragmentContext } from '@philips-internal/oswald-ui-core/lib/component/context';
import { SubmitEvent, ValidateEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { HttpService } from '@philips-internal/oswald-ui-core/lib/services';
import { APPLICATION_ENDPOINTS } from '../../../constants/application-endpoints';
import { showToastNotificationError, showToastNotificationSuccess } from '../../../utils/toast-notification-utils';
import UserContext from '../../../utils/user-context';
import { checkValidEmailList } from '../../../utils/user-util';
import BaseController from '../../BaseController';

interface ContextData {
  data: MailParams;
}

interface MailParams {
  contents: Record<string, Value>,
  encounterId: number,
  groupCode: number | string,
  status: string,
  summarySequence: number,
  unifiedReport: boolean
}

@Controller({ component: 'send-email-summary', fragment: 'send-mail-summary' })
class MyChartDischargeSummaryEmailController extends BaseController {

  @Inject('panel-loader-send-mail')
    panelLoaderSendMailApi!: PanelAPI;

  @Inject('send-mail-panel')
    sendMailPanelApi!: PanelAPI;

  @Inject('send-mail-field-notes')
    sendMailFieldNotesApi!: TextAreaAPI;

  @Inject('field-list-dropdown-send-mail')
    fieldListDropdownSendMailApi!: FieldListAPI;

  @Inject(ContextType.SUBMITTABLE_FRAGMENT)
    submittableFragmentContext!: SubmittableFragmentContext;

  @Inject('button-send-mail')
    buttonSendMailAPI!: ButtonAPI;

  @Inject('form-dropdown-send-mail')
    formDropdownSendMailAPI!: FormAPI;

  @OnValidate({ target: 'form-dropdown-send-mail' })
  handleFormDropdownSendMailValidate(event: ValidateEvent): void {
    this.buttonSendMailAPI.setEnabled(event.valid);
  }

  @OnClick({ target: ['button-send-mail'] })
  onConfirmSendMailClick(): void {
    interface EmailFromFieldList {
      email: string;
    }

    if (checkValidEmailList(this.fieldListDropdownSendMailApi.getValue())) {
      const mailList = this.fieldListDropdownSendMailApi.getValue() as unknown as EmailFromFieldList[];
      const context = this.context as unknown as ContextData;
      const mailParams = context.data;
      const body = {
        contents: mailParams.contents,
        notes: this.sendMailFieldNotesApi.getValue(),
        receivers: mailList.map(item => { return item.email }),
      };
      const summaryReportParameters = {
        groupCode: mailParams.groupCode,
        patientId: UserContext.patientId,
      };
      const summaryUnifiedReportParameters = {
        encounterId: mailParams.encounterId,
        groupCode: mailParams.groupCode,
        patientId: UserContext.patientId,
        status: mailParams.status,
        summarySequence: mailParams.summarySequence,
      };
      const parameters = mailParams.unifiedReport ? summaryUnifiedReportParameters : summaryReportParameters;
      const endpoint = mailParams.unifiedReport
        ? APPLICATION_ENDPOINTS.POST_MC_SUMMARY_UNIFIED_REPORT_EMAIL
        : APPLICATION_ENDPOINTS.POST_MC_SUMMARY_REPORT_EMAIL;

      this.setLoaderSendMailDropDown(true);
      HttpService.post(endpoint, parameters, { body })
        .then(() => {
          this.submittableFragmentContext.submit(new SubmitEvent());
          showToastNotificationSuccess('patient.email-sended');
        })
        .catch(() => {
          showToastNotificationError('newaccount.backend.error.sending.email');
          this.setLoaderSendMailDropDown(false);
        });
    }
  }

  setLoaderSendMailDropDown(showLoader: boolean): void {
    if (showLoader) {
      this.context.set({ formDropdownSendEmailData: this.formDropdownSendMailAPI.getRecord() })
    }

    this.sendMailPanelApi.setVisible(!showLoader);
    this.panelLoaderSendMailApi.setVisible(showLoader);

    if (!showLoader) {
      const previousFormData = this.context.get('formDropdownSendEmailData') as Record<string, Value>;
      if (previousFormData) {
        this.formDropdownSendMailAPI.setData(previousFormData);
      }
    }
  }
}

export default MyChartDischargeSummaryEmailController;